/**
 * Multi-select for user scope grants (sites, work orders).
 *
 * A user can hold many sites AND many work orders, so a single-value `<Select>`
 * cannot express the assignment. This follows the house `Set<number>` + `Checkbox`
 * pattern already used by AddSitesDialog and WorkOrderCreateWizard rather than
 * pulling in a combobox dependency.
 *
 * The selected ids are kept sorted ascending, which matters beyond tidiness: the
 * caller sends `value[0]` as the legacy single-site field, and the backend derives
 * the same primary from the lowest id when a user has no surviving primary. Sorting
 * here keeps the two ends agreeing.
 */

import { useMemo, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ScopeOption {
  readonly id: number;
  readonly label: string;
  /** Secondary text shown next to the label (site code, client name, …). */
  readonly sublabel?: string | undefined;
  /** False renders the row muted with an "inactive" tag — the grant still stands. */
  readonly isActive?: boolean | undefined;
}

export interface ScopeMultiSelectProps {
  /** Prefix for every `data-testid` and DOM id this renders. */
  readonly idPrefix: string;
  readonly options: readonly ScopeOption[];
  readonly value: readonly number[];
  readonly onChange: (next: number[]) => void;
  readonly disabled?: boolean | undefined;
  readonly loading?: boolean | undefined;
  readonly filterPlaceholder: string;
  /** Shown when the option list is empty (nothing to assign at all). */
  readonly emptyText: string;
  /** Shown when the filter matches nothing. */
  readonly noMatchText: string;
}

export function ScopeMultiSelect({
  idPrefix,
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  filterPlaceholder,
  emptyText,
  noMatchText,
}: ScopeMultiSelectProps): JSX.Element {
  const [filter, setFilter] = useState('');

  const selected = useMemo(() => new Set(value), [value]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.sublabel ?? '').toLowerCase().includes(needle),
    );
  }, [options, filter]);

  // Chips are rendered from `value`, not from `options`, so a grant whose option is
  // missing from the reference list (deactivated, or beyond the fetched page) still
  // shows up and can be removed rather than silently disappearing on the next save.
  const labelById = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of options) m.set(o.id, o.label);
    return m;
  }, [options]);

  function toggle(id: number): void {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange([...next].sort((a, b) => a - b));
  }

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-multiselect`}>
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${idPrefix}-chips`}>
          {[...value]
            .sort((a, b) => a - b)
            .map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs"
                data-testid={`${idPrefix}-chip-${String(id)}`}
              >
                {labelById.get(id) ?? `#${String(id)}`}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    toggle(id);
                  }}
                  aria-label={`Remove ${labelById.get(id) ?? `#${String(id)}`}`}
                  data-testid={`${idPrefix}-chip-remove-${String(id)}`}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  ×
                </button>
              </span>
            ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              onChange([]);
            }}
            data-testid={`${idPrefix}-clear`}
            className="h-6 px-2 text-xs"
          >
            Clear all
          </Button>
        </div>
      )}

      {options.length > 5 && (
        <Input
          id={`${idPrefix}-filter`}
          type="search"
          value={filter}
          disabled={disabled}
          placeholder={filterPlaceholder}
          onChange={(e) => {
            setFilter(e.target.value);
          }}
          data-testid={`${idPrefix}-filter`}
        />
      )}

      <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-3">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && options.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid={`${idPrefix}-empty`}>
            {emptyText}
          </p>
        )}
        {!loading && options.length > 0 && visible.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid={`${idPrefix}-nomatch`}>
            {noMatchText}
          </p>
        )}
        {visible.map((o) => (
          <div key={o.id} className="flex items-center gap-3">
            <Checkbox
              id={`${idPrefix}-option-${String(o.id)}`}
              checked={selected.has(o.id)}
              disabled={disabled}
              onCheckedChange={() => {
                toggle(o.id);
              }}
              data-testid={`${idPrefix}-option-${String(o.id)}`}
            />
            <Label
              htmlFor={`${idPrefix}-option-${String(o.id)}`}
              className="cursor-pointer font-normal"
            >
              {o.label}
              {o.sublabel !== undefined && o.sublabel !== '' && (
                <span className="ml-2 text-xs text-muted-foreground">{o.sublabel}</span>
              )}
              {o.isActive === false && (
                <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
              )}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}
